import { ChangeEvent, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { APIHOST } from '../../common';
import { getToken } from '../auth/AuthContext';
import { useApp } from '../AppContext';
import { UpdateConfirmationRequest } from '../../types/updateConfirmationRequest';

interface ImportConfirmationsProps {
  open: boolean;
  onClose: () => void;
  onImported?: () => void;
}

type CsvRecord = Record<string, string>;

interface FailedImportRow {
  rowNumber: number;
  internalId: string;
  hotelConfirmationNumber: string;
  reason: string;
}

const parseCsv = (content: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];
    const nextChar = content[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentCell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentCell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ',') {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if (char === '\r') {
      continue;
    }

    if (char === '\n') {
      currentRow.push(currentCell);
      if (currentRow.some((v) => v.length > 0)) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentCell = '';
      continue;
    }

    currentCell += char;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    if (currentRow.some((v) => v.length > 0)) {
      rows.push(currentRow);
    }
  }

  return rows;
};

const rowsToObjects = (rows: string[][]): { headers: string[]; records: CsvRecord[] } => {
  if (rows.length === 0) {
    return { headers: [], records: [] };
  }

  const headers = rows[0].map((h) => h.trim());
  const records = rows.slice(1).map((row) => {
    const record: CsvRecord = {};
    headers.forEach((header, idx) => {
      record[header] = (row[idx] ?? '').trim();
    });
    return record;
  });

  return { headers, records };
};

export default function ImportConfirmations({ open, onClose, onImported }: ImportConfirmationsProps) {
  const { setSnackbarInput } = useApp();
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [records, setRecords] = useState<CsvRecord[]>([]);
  const [internalIdColumn, setInternalIdColumn] = useState('');
  const [hotelConfirmationColumn, setHotelConfirmationColumn] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [failedRows, setFailedRows] = useState<FailedImportRow[]>([]);

  const totalRows = records.length;
  const importPercent = useMemo(() => {
    if (totalRows === 0) {
      return 0;
    }
    return Math.round((processedCount / totalRows) * 100);
  }, [processedCount, totalRows]);

  const internalIdPreview = useMemo(() => {
    if (!internalIdColumn || records.length === 0) {
      return '';
    }
    const sampleValues = records
      .slice(0, 3)
      .map((row) => (row[internalIdColumn] ?? '').trim() || '(blank)');
    return `${sampleValues.join(', ')} ...`;
  }, [internalIdColumn, records]);

  const internalIdSampleValues = useMemo(() => {
    if (!internalIdColumn || records.length === 0) {
      return [] as string[];
    }

    return records
      .slice(0, 3)
      .map((row) => (row[internalIdColumn] ?? '').trim());
  }, [internalIdColumn, records]);

  const internalIdLooksValid = useMemo(() => {
    if (internalIdSampleValues.length === 0) {
      return false;
    }

    const hasAlphaNumeric = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]+$/;
    return internalIdSampleValues.every((value) => hasAlphaNumeric.test(value));
  }, [internalIdSampleValues]);

  const hotelConfirmationPreview = useMemo(() => {
    if (!hotelConfirmationColumn || records.length === 0) {
      return '';
    }
    const sampleValues = records
      .slice(0, 3)
      .map((row) => (row[hotelConfirmationColumn] ?? '').trim() || '(blank)');
    return `${sampleValues.join(', ')} ...`;
  }, [hotelConfirmationColumn, records]);

  const resetState = () => {
    setFileName('');
    setHeaders([]);
    setRecords([]);
    setInternalIdColumn('');
    setHotelConfirmationColumn('');
    setError('');
    setImporting(false);
    setProcessedCount(0);
    setSuccessCount(0);
    setFailedCount(0);
    setFailedRows([]);
  };

  const handleClose = () => {
    if (!importing) {
      resetState();
      onClose();
    } else {
        setSnackbarInput({
          message: 'Please wait for the import to finish before closing.',
          severity: 'info',
        });
    }
  };

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    setError('');
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      const { headers: parsedHeaders, records: parsedRecords } = rowsToObjects(parsed);

      if (parsedHeaders.length === 0) {
        throw new Error('No CSV headers found');
      }

      setFileName(file.name);
      setHeaders(parsedHeaders);
      setRecords(parsedRecords);
      setInternalIdColumn(parsedHeaders[0] || '');
      setHotelConfirmationColumn(parsedHeaders[1] || parsedHeaders[0] || '');
      setProcessedCount(0);
      setSuccessCount(0);
      setFailedCount(0);
      setFailedRows([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
      setHeaders([]);
      setRecords([]);
      setFailedRows([]);
    } finally {
      event.target.value = '';
    }
  };

  const handleImport = async () => {
    if (!internalIdColumn || !hotelConfirmationColumn) {
      setError('Please select both CSV columns.');
      return;
    }

    const token = getToken();
    if (!token) {
      setError('No authentication token found');
      return;
    }

    setImporting(true);
    setError('');
    setProcessedCount(0);
    setSuccessCount(0);
    setFailedCount(0);
    setFailedRows([]);

    let nextSuccess = 0;
    let nextFailed = 0;
    const nextFailedRows: FailedImportRow[] = [];

    for (const [index, row] of records.entries()) {
      const internalId = (row[internalIdColumn] ?? '').trim();
      const hotelConfirmationNumber = (row[hotelConfirmationColumn] ?? '').trim();
      const rowNumber = index + 2;

      if (!internalId) {
        nextFailed += 1;
        setFailedCount(nextFailed);
        nextFailedRows.push({
          rowNumber,
          internalId,
          hotelConfirmationNumber,
          reason: 'Missing internalId value',
        });
        setProcessedCount((prev) => prev + 1);
        continue;
      }

      try {
        const body: UpdateConfirmationRequest = {
          data: {
            internalId,
            hotelConfirmationNumber,
          },
        };

        const response = await fetch(`${APIHOST}/api/hotel-admin/update-confirmation`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const responseData = await response.json();
          nextFailed += 1;
          setFailedCount(nextFailed);
          nextFailedRows.push({
            rowNumber,
            internalId,
            hotelConfirmationNumber,
            reason: responseData?.error?.message || `HTTP ${response.status}`,
          });
        } else {
          nextSuccess += 1;
          setSuccessCount(nextSuccess);
        }
      } catch {
        nextFailed += 1;
        setFailedCount(nextFailed);
        nextFailedRows.push({
          rowNumber,
          internalId,
          hotelConfirmationNumber,
          reason: 'Network or unexpected error',
        });
      } finally {
        setProcessedCount((prev) => prev + 1);
      }
    }

    setImporting(false);
    setFailedRows(nextFailedRows);
    setSnackbarInput({
      message: `Import complete: ${nextSuccess} saved, ${nextFailed} failed`,
      severity: nextFailed > 0 ? 'warning' : 'success',
    });
    onImported?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Import Confirmations</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {!fileName && (
            <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Choose a CSV file that contains the room confirmations we provided when exporting the booking list, along with the confirmation numbers provided by your hotel.<br /><br /> The CSV should have a header row with column names. You will be asked to identify which columns contain the relevant data in the next step.<br/>
                </Typography>
                <Button variant="outlined" component="label" disabled={importing}>
                    Select CSV File
                    <input type="file" hidden accept=".csv,text/csv" onChange={handleFileSelected} />
                </Button>
            </Box>
          )}

          {fileName && (
            <Alert severity="info">
              File: {fileName}<br />
              Columns found: {headers.length}<br />
              Data rows: {totalRows}
            </Alert>
          )}

          {headers.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary">➊ Choose the name of the column that contains the room confirmations we provided when exporting the booking list. These confirmations are 8 characters long with a mix of letters and numbers.</Typography>
              <Alert severity="warning">
                <Typography variant="subtitle2" color="text.warning">This is the RoomConfirmation column from our export, NOT the order ID</Typography>
              </Alert>
              <FormControl fullWidth size="small">
                <InputLabel>CityWide Room Confirmation Column</InputLabel>
                <Select
                  value={internalIdColumn}
                  label="CityWide Room Confirmation Column"
                  disabled={importing}
                  onChange={(e) => setInternalIdColumn(e.target.value)}
                >
                  {headers.map((header) => (
                    <MenuItem key={`internal-${header}`} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {internalIdPreview && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color={'text.secondary'}>
                      Samples: {internalIdPreview}
                    </Typography>
                    {!internalIdLooksValid && (
                      <Typography variant="subtitle2" color="error" display="block">
                         ❌ The "{internalIdColumn}" column doesn't look like it contains valid room confirmations. Please select the correct column.
                      </Typography>
                    )}
                    {internalIdLooksValid && (
                      <Typography variant="subtitle2" color="success.main" display="block">
                        ✅ The "{internalIdColumn}" column appears to contain valid room confirmations.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Box sx={{ pt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">➋ Choose the name of the column that contains the room confirmation numbers provided by the hotel. These will be added to our system for easier tracking.<br/></Typography>
                <Box pt={2}>
                    <FormControl fullWidth size="small" >
                        <InputLabel>Hotel Confirmation Column</InputLabel>
                        <Select
                        value={hotelConfirmationColumn}
                        label="Hotel Confirmation Column"
                        disabled={importing}
                        onChange={(e) => setHotelConfirmationColumn(e.target.value)}
                        >
                        {headers.map((header) => (
                            <MenuItem key={`hotel-${header}`} value={header}>
                            {header}
                            </MenuItem>
                        ))}
                        </Select>
                    </FormControl>
                </Box>
              </Box>
              {hotelConfirmationPreview && (
                <Typography variant="caption" color="text.secondary">
                  Samples: {hotelConfirmationPreview}
                </Typography>
              )}
            </>
          )}

          {importing && (
            <Box>
              <LinearProgress variant="determinate" value={importPercent} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processing {processedCount}/{totalRows} rows
              </Typography>
            </Box>
          )}

          {processedCount > 0 && !importing && (
            <Stack spacing={1}>
              <Alert severity={failedCount > 0 ? 'warning' : 'success'}>
                Import Summary: {successCount} successful updates, {failedCount} failed updates.
              </Alert>
              {failedRows.length > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 1, p: 1.5, maxHeight: 220, overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                    Failed rows
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>InternalId</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>hotelConfirmationNumber</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedRows.map((failedRow) => (
                        <TableRow key={`${failedRow.rowNumber}-${failedRow.internalId}-${failedRow.hotelConfirmationNumber}`}>
                          <TableCell>{failedRow.internalId || '(blank)'}</TableCell>
                          <TableCell>{failedRow.hotelConfirmationNumber || '(blank)'}</TableCell>
                          <TableCell>{failedRow.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Close</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importing || headers.length === 0 || totalRows === 0 || !internalIdColumn || !hotelConfirmationColumn || !internalIdLooksValid}
        >
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}